import { useCallback, useEffect, useRef, useState } from "react";

const EDGE_TOLERANCE = 4;

const getScrollMetrics = (element) => {
  if (!element) {
    return {
      hasOverflow: false,
      canScrollLeft: false,
      canScrollRight: false,
    };
  }

  const maxScrollLeft = Math.max(element.scrollWidth - element.clientWidth, 0);
  const scrollLeft = element.scrollLeft;

  return {
    hasOverflow: maxScrollLeft > EDGE_TOLERANCE,
    canScrollLeft: scrollLeft > EDGE_TOLERANCE,
    canScrollRight: scrollLeft < maxScrollLeft - EDGE_TOLERANCE,
  };
};

const isSameScrollState = (previousState, nextState) =>
  previousState.hasOverflow === nextState.hasOverflow &&
  previousState.canScrollLeft === nextState.canScrollLeft &&
  previousState.canScrollRight === nextState.canScrollRight;

const useTableOverflowControls = ({ dataSource, columns, enabled = true, step = 18 }) => {
  const columnSignature = columns
    .map((column, index) => column?.key || column?.dataIndex || column?.title || `column-${index}`)
    .join("|");
  const rowCount = Array.isArray(dataSource) ? dataSource.length : 0;
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const directionRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const findScrollableBody = useCallback(() => {
    const host = containerRef.current;
    if (!host) {
      return null;
    }

    return (
      host.querySelector(".ant-table-body") ||
      host.querySelector(".ant-table-content") ||
      host.querySelector(".ant-table-container")
    );
  }, []);

  const updateScrollState = useCallback(() => {
    if (!enabled) {
      const resetState = {
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false,
      };
      setScrollState((previousState) =>
        isSameScrollState(previousState, resetState) ? previousState : resetState
      );
      return;
    }

    const nextState = getScrollMetrics(findScrollableBody());
    setScrollState((previousState) =>
      isSameScrollState(previousState, nextState) ? previousState : nextState
    );
  }, [enabled, findScrollableBody]);

  const stopAutoScroll = useCallback(() => {
    directionRef.current = null;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    updateScrollState();

    refreshTimeoutRef.current = setTimeout(() => {
      updateScrollState();
    }, 120);
  }, [updateScrollState]);

  const runAutoScroll = useCallback(() => {
    const scrollableBody = findScrollableBody();
    if (!scrollableBody || !directionRef.current) {
      animationFrameRef.current = null;
      return;
    }

    const metrics = getScrollMetrics(scrollableBody);
    if (
      (directionRef.current === "left" && !metrics.canScrollLeft) ||
      (directionRef.current === "right" && !metrics.canScrollRight)
    ) {
      stopAutoScroll();
      updateScrollState();
      return;
    }

    const nextScrollLeft =
      scrollableBody.scrollLeft + (directionRef.current === "right" ? step : -step);

    scrollableBody.scrollTo({
      left: nextScrollLeft,
      behavior: "auto",
    });

    updateScrollState();
    animationFrameRef.current = requestAnimationFrame(runAutoScroll);
  }, [findScrollableBody, step, stopAutoScroll, updateScrollState]);

  const startAutoScroll = useCallback(
    (direction) => {
      const scrollableBody = findScrollableBody();
      if (!scrollableBody) {
        return;
      }

      const metrics = getScrollMetrics(scrollableBody);
      if ((direction === "left" && !metrics.canScrollLeft) || (direction === "right" && !metrics.canScrollRight)) {
        updateScrollState();
        return;
      }

      directionRef.current = direction;
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(runAutoScroll);
      }
    },
    [findScrollableBody, runAutoScroll, updateScrollState]
  );

  useEffect(() => {
    scheduleRefresh();

    const scrollableBody = findScrollableBody();
    if (!scrollableBody) {
      return stopAutoScroll;
    }

    const handleScroll = () => updateScrollState();
    scrollableBody.addEventListener("scroll", handleScroll, { passive: true });

    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
        scheduleRefresh();
      })
      : null;

    resizeObserver?.observe(scrollableBody);

    const host = containerRef.current;
    if (host) {
      resizeObserver?.observe(host);
    }

    const parentElement = host?.parentElement;
    if (parentElement) {
      resizeObserver?.observe(parentElement);
    }

    const handleWindowResize = () => {
      scheduleRefresh();
    };
    window.addEventListener("resize", handleWindowResize);

    const mutationObserver = typeof MutationObserver !== "undefined" && host
      ? new MutationObserver(() => {
        scheduleRefresh();
      })
      : null;

    mutationObserver?.observe(host, {
      childList: true,
      subtree: true,
    });

    return () => {
      stopAutoScroll();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      scrollableBody.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleWindowResize);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [rowCount, columnSignature, findScrollableBody, scheduleRefresh, stopAutoScroll, updateScrollState]);

  useEffect(
    () => () => {
      stopAutoScroll();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    },
    [stopAutoScroll]
  );

  return {
    containerRef,
    hasOverflow: scrollState.hasOverflow,
    canScrollLeft: scrollState.canScrollLeft,
    canScrollRight: scrollState.canScrollRight,
    showLeftHint: scrollState.hasOverflow && scrollState.canScrollLeft,
    showRightHint: scrollState.hasOverflow && scrollState.canScrollRight,
    startAutoScroll,
    stopAutoScroll,
  };
};

export default useTableOverflowControls;
